/**
 * ServicesCatalogSection Component
 * Feature: 008-admin-provider-editor (Services Catalog Extension)
 * 
 * Allows admins to manage the provider's services catalog:
 * - Add new services with name, description, pricing
 * - Edit existing services
 * - Reorder services by displayOrder
 * - Toggle active/inactive status
 * - Delete services (soft delete)
 */

import { useState } from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import { Plus, GripVertical, Edit2, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import type { ProviderEditorFormData } from '../schemas';

type ServicesCatalogSectionProps = {
  control: Control<ProviderEditorFormData>;
};

type ServiceFormState = {
  serviceName: string;
  serviceDescription: string;
  servicePrice: string;
  priceType: string;
  priceRangeIndicator: string;
  isActive: boolean;
};

export function ServicesCatalogSection({ control }: ServicesCatalogSectionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'servicesCatalog',
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newService, setNewService] = useState<ServiceFormState>({
    serviceName: '',
    serviceDescription: '',
    servicePrice: '',
    priceType: 'per hour',
    priceRangeIndicator: '$$',
    isActive: true,
  });

  const handleAddService = () => {
    if (!newService.serviceName.trim()) {
      return;
    }

    append({
      id: `temp_${Date.now()}`,
      serviceName: newService.serviceName,
      serviceDescription: newService.serviceDescription || '',
      servicePrice: newService.servicePrice || '',
      priceType: newService.priceType || '',
      priceRangeIndicator: newService.priceRangeIndicator as '$' | '$$' | '$$$' | '$$$$' | null,
      isActive: newService.isActive,
      displayOrder: fields.length,
      isNew: true,
    });

    // Reset form
    setNewService({
      serviceName: '',
      serviceDescription: '',
      servicePrice: '',
      priceType: 'per hour',
      priceRangeIndicator: '$$',
      isActive: true,
    });
    setIsAddingNew(false);
  };

  const handleCancelAdd = () => {
    setNewService({
      serviceName: '',
      serviceDescription: '',
      servicePrice: '',
      priceType: 'per hour',
      priceRangeIndicator: '$$',
      isActive: true,
    });
    setIsAddingNew(false);
  };

  const moveUp = (index: number) => {
    if (index > 0) {
      move(index, index - 1);
    }
  };

  const moveDown = (index: number) => {
    if (index < fields.length - 1) {
      move(index, index + 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Services Catalog</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage the services offered by this provider
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setIsAddingNew(true)}
          disabled={isAddingNew}
          className="bg-[#026857] hover:bg-[#025247]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      {/* Add New Service Form */}
      {isAddingNew && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
          <h4 className="font-medium text-gray-900">New Service</h4>

          <div className="space-y-4">
            {/* Service Name */}
            <div>
              <Label htmlFor="new-service-name" className="text-sm font-medium text-gray-700">
                Service Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-service-name"
                value={newService.serviceName}
                onChange={(e) => setNewService({ ...newService, serviceName: e.target.value })}
                placeholder="e.g., Personal Care Assistance"
                maxLength={255}
              />
            </div>

            {/* Service Description */}
            <div>
              <Label htmlFor="new-service-description" className="text-sm font-medium text-gray-700">
                Description
              </Label>
              <Textarea
                id="new-service-description"
                value={newService.serviceDescription}
                onChange={(e) => setNewService({ ...newService, serviceDescription: e.target.value })}
                placeholder="Brief description of the service"
                rows={3}
                maxLength={500}
              />
            </div>

            {/* Pricing Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="new-service-price" className="text-sm font-medium text-gray-700">
                  Price
                </Label>
                <Input
                  id="new-service-price"
                  value={newService.servicePrice}
                  onChange={(e) => setNewService({ ...newService, servicePrice: e.target.value })}
                  placeholder="$28 or Contact"
                  maxLength={50}
                />
              </div>

              <div>
                <Label htmlFor="new-price-type" className="text-sm font-medium text-gray-700">
                  Price Type
                </Label>
                <Input
                  id="new-price-type"
                  value={newService.priceType}
                  onChange={(e) => setNewService({ ...newService, priceType: e.target.value })}
                  placeholder="per hour, per session"
                  maxLength={50}
                />
              </div>

              <div>
                <Label htmlFor="new-price-range" className="text-sm font-medium text-gray-700">
                  Price Range
                </Label>
                <select
                  id="new-price-range"
                  value={newService.priceRangeIndicator}
                  onChange={(e) => setNewService({ ...newService, priceRangeIndicator: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="$">$ - Budget-friendly</option>
                  <option value="$$">$$ - Moderate</option>
                  <option value="$$$">$$$ - Premium</option>
                  <option value="$$$$">$$$$ - Luxury</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelAdd}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddService}
              disabled={!newService.serviceName.trim()}
              className="bg-[#026857] hover:bg-[#025247]"
            >
              <Check className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        </div>
      )}

      {/* Services List */}
      {fields.length > 0 ? (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    type="button"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(index)}
                    disabled={index === fields.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move down"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                </div>

                {/* Service Content */}
                <div className="flex-1 min-w-0">
                  <Controller
                    name={`servicesCatalog.${index}.serviceName`}
                    control={control}
                    render={({ field: serviceNameField }) => (
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {serviceNameField.value}
                      </h4>
                    )}
                  />

                  <Controller
                    name={`servicesCatalog.${index}.serviceDescription`}
                    control={control}
                    render={({ field: descField }) => (
                      <>
                        {descField.value && (
                          <p className="text-sm text-gray-600 mb-2">
                            {descField.value}
                          </p>
                        )}
                      </>
                    )}
                  />

                  <div className="flex items-center gap-4 text-sm">
                    <Controller
                      name={`servicesCatalog.${index}.servicePrice`}
                      control={control}
                      render={({ field: priceField }) => (
                        <>
                          {priceField.value && (
                            <span className="text-gray-700">
                              <strong>{priceField.value}</strong>
                            </span>
                          )}
                        </>
                      )}
                    />

                    <Controller
                      name={`servicesCatalog.${index}.priceType`}
                      control={control}
                      render={({ field: typeField }) => (
                        <>
                          {typeField.value && (
                            <span className="text-gray-600">{typeField.value}</span>
                          )}
                        </>
                      )}
                    />

                    <Controller
                      name={`servicesCatalog.${index}.priceRangeIndicator`}
                      control={control}
                      render={({ field: rangeField }) => (
                        <>
                          {rangeField.value && (
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700 font-semibold">
                              {rangeField.value}
                            </span>
                          )}
                        </>
                      )}
                    />

                    <Controller
                      name={`servicesCatalog.${index}.isActive`}
                      control={control}
                      render={({ field: activeField }) => (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            activeField.value
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {activeField.value ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                    title="Edit service"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Remove service"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Edit Form (Expanded) */}
              {editingIndex === index && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        Service Name <span className="text-red-500">*</span>
                      </Label>
                      <Controller
                        name={`servicesCatalog.${index}.serviceName`}
                        control={control}
                        render={({ field }) => (
                          <Input {...field} maxLength={255} />
                        )}
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Active Status</Label>
                      <Controller
                        name={`servicesCatalog.${index}.isActive`}
                        control={control}
                        render={({ field }) => (
                          <select
                            value={field.value ? 'true' : 'false'}
                            onChange={(e) => field.onChange(e.target.value === 'true')}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Description</Label>
                    <Controller
                      name={`servicesCatalog.${index}.serviceDescription`}
                      control={control}
                      render={({ field }) => (
                        <Textarea {...field} value={field.value || ''} rows={3} maxLength={500} />
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Price</Label>
                      <Controller
                        name={`servicesCatalog.${index}.servicePrice`}
                        control={control}
                        render={({ field }) => (
                          <Input {...field} value={field.value || ''} maxLength={50} />
                        )}
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Price Type</Label>
                      <Controller
                        name={`servicesCatalog.${index}.priceType`}
                        control={control}
                        render={({ field }) => (
                          <Input {...field} value={field.value || ''} maxLength={50} />
                        )}
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Price Range</Label>
                      <Controller
                        name={`servicesCatalog.${index}.priceRangeIndicator`}
                        control={control}
                        render={({ field }) => (
                          <select
                            value={field.value || '$$'}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <option value="$">$ - Budget-friendly</option>
                            <option value="$$">$$ - Moderate</option>
                            <option value="$$$">$$$ - Premium</option>
                            <option value="$$$$">$$$$ - Luxury</option>
                          </select>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingIndex(null)}
                    >
                      Done Editing
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !isAddingNew && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <p className="text-gray-600 mb-4">No services added yet</p>
            <Button
              type="button"
              onClick={() => setIsAddingNew(true)}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Service
            </Button>
          </div>
        )
      )}
    </div>
  );
}

